import React, { useState, useEffect } from 'react';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';
import { t } from '../utils/translations';
import { formatMonthlyStudyTime } from '../utils/timeUtils';
import { formatSubscriptionValue } from '../utils/currencyUtils';

const SubscriptionManagement: React.FC = () => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const status = await subscriptionService.getStatus();
      setSubscriptionStatus(status);
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      toast.error(t('subscription.failedToLoadInfo'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    // Show the cancellation instructions in a modal without making backend call
    const modalDiv = document.createElement('div');
    modalDiv.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modalDiv.innerHTML = `
      <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">
            📧 Instruções para Cancelamento
          </h3>
          <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Para cancelar sua assinatura, envie um e-mail para clesiosmatos@gmail.com solicitando o cancelamento. Inclua o e-mail da sua conta no pedido.
            </p>
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
              <p class="text-sm text-blue-700 dark:text-blue-300">
                <strong>📧 E-mail de contato:</strong><br>
                <a href="mailto:clesiosmatos@gmail.com" class="text-blue-600 dark:text-blue-400 underline">
                  clesiosmatos@gmail.com
                </a>
              </p>
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
              <p class="text-sm text-gray-700 dark:text-gray-300">
                <strong>💡 Dica:</strong> Envie um e-mail com o assunto "Cancelamento de Assinatura" e inclua o e-mail da sua conta registrada.
              </p>
            </div>
          </div>
          <div class="items-center px-4 py-3">
            <button
              id="ok-btn"
              class="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Entendi
            </button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(modalDiv);
    
    // Add click handler to close modal
    const closeModal = () => {
      document.body.removeChild(modalDiv);
    };
    
    modalDiv.querySelector('#ok-btn')?.addEventListener('click', closeModal);
    modalDiv.addEventListener('click', (e) => {
      if (e.target === modalDiv) closeModal();
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            {t('subscription.subscriptionManagement')}
          </h3>
          <div className="flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionStatus) {
    return null;
  }

  const isPremium = subscriptionStatus.subscriptionTier === 'premium';
  const isActive = subscriptionStatus.isSubscriptionActive;
  const hasOneDayPass = subscriptionStatus.oneDayPassActive && subscriptionStatus.oneDayPassExpiresAt;
  const oneDayPassExpired = subscriptionStatus.oneDayPassExpiresAt && 
    new Date(subscriptionStatus.oneDayPassExpiresAt) < new Date();
  
  // User is effectively active if they have an active subscription OR an active one-day pass
  const isEffectivelyActive = isActive || (hasOneDayPass && !oneDayPassExpired);

  return (
    <div className="space-y-6">
      {/* One-Day Pass Alert - Show if active */}
      {hasOneDayPass && !oneDayPassExpired && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg p-3 sm:p-4 shadow-md">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs sm:text-sm font-bold text-yellow-900 dark:text-yellow-200 flex items-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Passe de 1 Dia Ativo!
              </h3>
              <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-yellow-800 dark:text-yellow-300">
                <p className="font-medium">
                  Você tem acesso Premium temporário até{' '}
                  <strong className="text-yellow-900 dark:text-yellow-200 block sm:inline mt-1 sm:mt-0">
                    {new Date(subscriptionStatus.oneDayPassExpiresAt!).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </strong>
                </p>
                <p className="mt-1 text-xs">
                  Aproveite ao máximo todos os recursos Premium enquanto seu passe está ativo! 🎉
                </p>
              </div>
              {/* Time remaining indicator */}
              <div className="mt-2 sm:mt-3 bg-white dark:bg-gray-800 rounded-md p-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">Tempo restante:</span>
                  <span className="font-semibold text-yellow-700 dark:text-yellow-300 text-sm sm:text-base">
                    {(() => {
                      const now = new Date();
                      const expiry = new Date(subscriptionStatus.oneDayPassExpiresAt!);
                      const diff = expiry.getTime() - now.getTime();
                      const hours = Math.floor(diff / (1000 * 60 * 60));
                      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                      return `${hours}h ${minutes}m`;
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan - Enhanced Design */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
              <div className="w-full sm:w-auto">
                <dt className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{t('subscription.currentPlan')}</dt>
                <dd className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                    isPremium && isEffectivelyActive 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' 
                      : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                  }`}>
                    {isPremium && isEffectivelyActive ? (
                      <><svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                      {t('subscription.premium')}</>
                    ) : (
                      <><svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                      {t('subscription.free')}</>
                    )}
                  </span>
                  {!isEffectivelyActive && isPremium && !hasOneDayPass && (
                    <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                      {t('subscription.cancelled')}
                    </span>
                  )}
                  {subscriptionStatus.subscriptionCancelled && isActive && !hasOneDayPass && (
                    <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                      Cancelada
                    </span>
                  )}
                </dd>
              </div>
            </div>

            {/* One-Day Pass Status Badge */}
            {hasOneDayPass && !oneDayPassExpired && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center text-sm">
                  <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                    Acesso via Passe de 1 Dia
                  </span>
                </div>
                <p className="mt-1 ml-7 text-xs text-gray-600 dark:text-gray-400">
                  Este é um acesso temporário que expira em{' '}
                  {new Date(subscriptionStatus.oneDayPassExpiresAt!).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            )}

            {/* Regular Subscription Details */}
            {isPremium && isActive && !hasOneDayPass && subscriptionStatus.subscriptionExpiresAt && (
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <div>
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Data de Renovação
                    </dt>
                    <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                      {new Date(subscriptionStatus.subscriptionExpiresAt).toLocaleDateString('pt-BR')}
                    </dd>
                  </div>
                  {subscriptionStatus.subscriptionValue && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Valor da Assinatura
                      </dt>
                      <dd className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatSubscriptionValue(subscriptionStatus.subscriptionValue)}
                        {subscriptionStatus.subscriptionInterval && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                            /{subscriptionStatus.subscriptionInterval === 'year' ? 'ano' : 'mês'}
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Cancellation Notice */}
          {isPremium && isActive && subscriptionStatus.subscriptionCancelled && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Assinatura Cancelada
                  </h3>
                  <div className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-yellow-700 dark:text-yellow-300">
                    <p>
                      Sua assinatura foi cancelada em{' '}
                      {subscriptionStatus.subscriptionCancelledAt && 
                        new Date(subscriptionStatus.subscriptionCancelledAt).toLocaleDateString('pt-BR')
                      }.
                    </p>
                    <p className="mt-1">
                      Você continuará tendo acesso aos recursos Premium até{' '}
                      <strong>{new Date(subscriptionStatus.subscriptionExpiresAt!).toLocaleDateString('pt-BR')}</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {isPremium && isActive && !subscriptionStatus.subscriptionCancelled && !hasOneDayPass && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={handleCancelSubscription}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                📧 Como Cancelar Assinatura
              </button>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Clique para ver as instruções de cancelamento por e-mail
              </p>
            </div>
          )}
    </div>
  );
};

export default SubscriptionManagement;
